<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { ElResponsiveTableProps, ResponsiveTableColumn, ResponsiveDataRow, ResponsiveDataCell } from './ElResponsiveTable.vue';
import ElServerSideMobileTable, { ElServerSideMobileTableProps } from './ElServerSideMobileTable.vue';
import ElServerSideTable, { DataControls, TableColumn, ServerSideTableProps, FilterStatus, SortStatus } from './ElServerSideTable.vue';
import { DataRow } from './commonTypes';
import { MobileTableFiltersStatus, MobileTableRow } from '@/table/ElMobileTable.vue';
import { DataCell } from './ElTableCell.vue';
import { ElTextCellColor, ElTextCellStyle } from '@/ElTextCell.vue';
import ElButton from '@/ElButton.vue';

export type ResponsiveServerSideTableColumn = Pick<TableColumn, 'title'> & {
  desktopBehaviour?: Pick<TableColumn, 'alignRight' | 'noSort'>;
  filter?: ResponsiveTableColumn['filter'];
} & Pick<ResponsiveTableColumn, 'mobileBehaviour'>;

export type ResponsiveDataControls = Pick<DataControls, 'paginationStatus' | 'filterStatus' | 'triggeredEvent'> &
  (
    | {
        tableType: 'desktop';
        sortStatus: SortStatus;
      }
    | {
        tableType: 'mobile';
        mobileFiltersStatus: MobileTableFiltersStatus;
      }
  );

export type ElServerSideResponsiveTableDataController = (
  args: ResponsiveDataControls,
) => Promise<{ data: ResponsiveDataRow[]; totalRows: number }>;

export type ElServerSideResponsiveTableProps = Pick<
  ElResponsiveTableProps,
  'mobileBehaviour' | 'desktopBehaviour' | 'sortBy' | 'color' | 'rowsSelectionMode' | 'rowsSelectionDisabled'
> & {
  columns: ResponsiveServerSideTableColumn[];
  dataController: ElServerSideResponsiveTableDataController;
  rowsPerPage?: number;
};

const mediaQuery = window.matchMedia('(max-width: 768px)');
const isMobile = ref(mediaQuery.matches);
const props = defineProps<ElServerSideResponsiveTableProps>();
const desktopTable = ref<InstanceType<typeof ElServerSideTable> | null>(null);
const mobileTable = ref<InstanceType<typeof ElServerSideMobileTable> | null>(null);

const onMatchMediaChange = (event: MediaQueryListEvent) => {
  isMobile.value = event.matches;
};
onMounted(() => {
  mediaQuery.addEventListener('change', onMatchMediaChange);
});
onUnmounted(() => {
  mediaQuery.removeEventListener('change', onMatchMediaChange);
});
const emit = defineEmits<{
  (event: 'rows-selected', rows: ResponsiveDataRow[]): void;
  (event: 'mobile:bulk:click', rows: ResponsiveDataRow[]): void;
  (event: 'mobile:destructive:click', rows: ResponsiveDataRow[]): void;
  (event: 'mobile:selection:exit'): void;
  (event: 'mobile:item:click', row: ResponsiveDataRow): void;
}>();

const responsiveDataCellToDataCell = (cell: ResponsiveDataCell): DataCell => {
  if (!cell.type) return { type: 'default', ...cell };

  switch (cell.type) {
    case 'default':
      return {
        type: 'default',
        mainIcon: cell.desktopBehaviour?.mainIcon,
        mainText: cell.mainText,
        mainTextStyle: cell.mainTextStyle,
        mainTextColor: cell.mainTextColor,
        subIcon: cell.desktopBehaviour?.subIcon,
        subText: cell.subText,
        avatar: cell.avatar,
        clickAction: cell.clickAction,
        longText: cell.desktopBehaviour?.longText,
        truncateSubText: cell.desktopBehaviour?.truncateSubText,
        sortableAndFilterableValue: cell.sortableAndFilterableValue,
      };
    case 'tag':
      return {
        type: 'tag',
        tag: {
          text: cell.desktopBehaviour?.tag.text ?? cell.text ?? '',
          color: cell.color,
        },
        subText: cell.desktopBehaviour?.subText,
        truncateSubText: cell.desktopBehaviour?.truncateSubText,
        sortableAndFilterableValue: cell.sortableAndFilterableValue,
      };
    case 'action':
      return {
        type: 'action',
        buttons: cell.buttons,
        iconButtons: cell.iconButtons,
        dropdown: cell.dropdown,
      };
  }
};

const responsiveDataRowToDesktopDataRow = (row: ResponsiveDataRow): DataRow => {
  return {
    id: row.id,
    rowNotSelectable: row.rowNotSelectable,
    cells: row.cells.map(responsiveDataCellToDataCell),
    relatedObject: row.relatedObject,
  };
};

const responsiveDataRowToMobileDataRow = (row: ResponsiveDataRow): MobileTableRow => {
  let mainText: string | undefined;
  let mainTextStyle: ElTextCellStyle | undefined;
  let mainTextColor: ElTextCellColor | undefined;
  let secondaryText: string | undefined;
  let tertiaryText: string | undefined;
  let amountText: string | undefined;
  let avatarLabel: string | undefined;
  let avatarPicture: string | undefined;
  let timeText: string | undefined;
  const tags: MobileTableRow['tags'] = [];
  let primaryAction: InstanceType<typeof ElButton>['$props'] | undefined;
  let secondaryAction: InstanceType<typeof ElButton>['$props'] | undefined;
  const details: MobileTableRow['details'] = [];

  for (const [cellIndex, cell] of row.cells.entries()) {
    const column = props.columns[cellIndex];
    if (!column) throw new Error('Missing column for cell at index ' + cellIndex);

    if (cell.type === 'default') {
      if (Array.isArray(column.mobileBehaviour?.position)) {
        for (const mapping of column.mobileBehaviour.position) {
          if (mapping.to === 'avatar') {
            details.push({
              label: mapping.withLabel,
              value: { type: 'avatar', ...cell.avatar },
            });
          } else {
            details.push({
              label: mapping.withLabel,
              value: { type: 'default', text: cell[mapping.take] ?? '' },
            });
          }
        }
      } else {
        if (cell.avatar) {
          details.push({
            label: column.title,
            value: { type: 'avatar', ...cell.avatar },
          });
        } else {
          details.push({
            label: column.title,
            value: { type: 'default', text: cell.mainText },
          });
        }
      }
    } else if (cell.type === 'tag') {
      details.push({
        label: column.title,
        value: { type: 'tag', text: cell.desktopBehaviour?.tag.text ?? cell.text ?? '', color: cell.color },
      });
    }

    const mobileColumn = column.mobileBehaviour;
    if (!mobileColumn && cell.type !== 'action') continue;
    if (cell.type === 'default') {
      if (Array.isArray(mobileColumn!.position)) {
        for (const mapping of mobileColumn!.position) {
          switch (mapping.to) {
            case 'time':
              timeText = cell[mapping.take];
              break;
            case 'mainText':
              mainText = cell[mapping.take];
              mainTextStyle = cell.mainTextStyle;
              mainTextColor = cell.mainTextColor;
              break;
            case 'secondaryRow':
              secondaryText = cell[mapping.take];
              break;
            case 'tertiaryRow':
              tertiaryText = cell[mapping.take];
              break;
            case 'amount':
              amountText = cell[mapping.take];
              break;
            case 'avatar':
              avatarLabel = cell.avatar?.label;
              avatarPicture = cell.avatar?.picture;
              break;
          }
        }
      } else {
        switch (mobileColumn!.position) {
          case 'mainText':
            mainText = cell.mainText;
            mainTextStyle = cell.mainTextStyle;
            mainTextColor = cell.mainTextColor;
            break;
          case 'secondaryRow':
            secondaryText = cell.subText ?? cell.mainText;
            break;
          case 'avatar':
            avatarLabel = cell.avatar?.label;
            avatarPicture = cell.avatar?.picture;
            break;
          case 'amount':
            amountText = cell.mobileBehaviour?.amountText ?? cell.mainText;
            break;
          case 'tertiaryRow':
            tertiaryText = cell.mobileBehaviour?.tertiaryText ?? cell.mainText;
            break;
          case 'tags':
            console.warn('Skipping tags mobile position on cell type default, for tags to be used in mobile table, use cell type tag');
            break;
          case 'time':
            timeText = cell.mobileBehaviour?.timeText ?? cell.mainText;
            break;
        }
        continue;
      }
    }

    if (cell.type === 'tag' && mobileColumn!.position === 'tags') {
      tags.push({
        key: cell.text,
        text: cell.desktopBehaviour?.tag.text ?? cell.text ?? '',
        color: cell.desktopBehaviour?.tag.color ?? cell.color,
      });
      continue;
    }

    if (cell.type == 'action') {
      if (cell.mobileBehaviour?.primary) {
        primaryAction = cell.mobileBehaviour.primary;
      }
      if (cell.mobileBehaviour?.secondary) {
        secondaryAction = cell.mobileBehaviour.secondary;
      }
    }
  }

  let actions: MobileTableRow['actions'];
  if (primaryAction) {
    actions = {
      primary: primaryAction,
      secondary: secondaryAction,
    };
  }

  if (!mainText) throw new Error('Missing mainText for mobile item');

  return {
    id: row.id,
    rowNotSelectable: row.rowNotSelectable,
    relatedObject: row.relatedObject,
    time: timeText,
    mainText: {
      label: mainText,
      style: mainTextStyle,
      color: mainTextColor,
    },
    secondaryRow: secondaryText,
    tertiaryRow: tertiaryText,
    tags,
    amount: amountText,
    avatar: avatarLabel || avatarPicture ? ({ label: avatarLabel, picture: avatarPicture } as MobileTableRow['avatar']) : undefined,
    trailingIcon: row.mobileBehaviour?.trailingIcon,
    actions,
    details,
  } satisfies MobileTableRow;
};

const mobileSelection = computed<ElServerSideMobileTableProps['selection']>(() => {
  if (props.rowsSelectionDisabled || !props.mobileBehaviour?.selection) return undefined;
  return {
    enabled: !props.rowsSelectionDisabled,
    selectionMode: props.rowsSelectionMode || 'multiple',
    destructiveAction: props.mobileBehaviour?.selection?.destructiveAction,
    bulkActionButton: props.mobileBehaviour?.selection?.bulkActionButton,
    cancelText: props.mobileBehaviour?.selection?.cancelText,
    selectedLabel: props.mobileBehaviour?.selection?.selectedLabel,
  };
});

const fetchedRows = ref<ResponsiveDataRow[]>([]);

const desktopDataController: ServerSideTableProps['dataController'] = data =>
  props.dataController({ tableType: 'desktop', ...data }).then(response => {
    if (data.triggeredEvent === 'paginate') {
      fetchedRows.value = [...fetchedRows.value, ...response.data];
    } else {
      fetchedRows.value = response.data;
    }
    return {
      data: response.data.map(responsiveDataRowToDesktopDataRow),
      totalRows: response.totalRows,
    };
  });

const mobileDataController: ElServerSideMobileTableProps['dataController'] = data =>
  props
    .dataController({
      tableType: 'mobile',
      ...data,
      mobileFiltersStatus: data.filterStatus,
      filterStatus: props.columns
        .map(column => {
          const filter = column.filter;
          if (!filter) return;
          switch (filter.type) {
            case 'FREE_SEARCH':
              return {
                id: filter.id,
                type: 'FREE_SEARCH',
                columnTitle: column.title,
                textSearch: data.filterStatus.freeSearch ?? '',
              } as FilterStatus;
            case 'DATE_RANGE':
              return {
                id: filter.id,
                type: 'DATE_RANGE',
                columnTitle: column.title,
                dateFrom: data.filterStatus.fromDate ?? 0,
              } satisfies FilterStatus;
            case 'MULTI_VALUE':
              const mobilePosition = filter.mobileBehaviour?.position ?? column.mobileBehaviour?.position;
              if (!mobilePosition) return;
              if (Array.isArray(mobilePosition)) {
                console.warn(
                  `Cannot map column ${column.title} mobile filter value since multiple mappings are defined, please specify the mobileBehaviour on the column.filter`,
                );
                return;
              }
              return {
                id: filter.id,
                type: 'MULTI_VALUE',
                columnTitle: column.title,
                textSearch: data.filterStatus.multiSelect[mobilePosition] ?? '',
              } satisfies FilterStatus;
            case 'RESET_FILTERS_BUTTON':
              return undefined;
          }
        })
        .filter((v: FilterStatus | undefined): v is FilterStatus => v !== undefined),
    })
    .then(response => {
      if (data.triggeredEvent === 'paginate') {
        fetchedRows.value = [...fetchedRows.value, ...response.data];
      } else {
        fetchedRows.value = response.data;
      }
      return {
        data: response.data.map(responsiveDataRowToMobileDataRow),
        totalRows: response.totalRows,
      };
    });

const mobileFilters = computed<ElServerSideMobileTableProps['filters']>(() => {
  const filters: ElServerSideMobileTableProps['filters'] = {};
  props.columns.forEach(column => {
    if (!column.filter) return;
    const mobileBehaviour = column.mobileBehaviour;
    if (!mobileBehaviour) return;

    if (column.filter.type === 'FREE_SEARCH') {
      if (!filters.freeSearch) {
        filters.freeSearch = {
          placeholder: column.title,
          initialValue: column.filter.initialValue,
        };
      }
      return;
    }

    if (column.filter.type === 'DATE_RANGE') {
      if (!filters.dateRange) {
        filters.dateRange = {
          enabled: true,
          label: column.title,
          initialValue: column.filter.initialValue,
        };
      }
      return;
    }

    if (column.filter.type === 'RESET_FILTERS_BUTTON') {
      filters.resetButton = true;
      return;
    }

    if (column.filter.type === 'MULTI_VALUE') {
      if (!filters.multiValue) {
        filters.multiValue = {};
      }
      if (Array.isArray(mobileBehaviour.position)) {
        const position = column.filter.mobileBehaviour?.position ?? mobileBehaviour.position.at(0)?.to;
        if (!position) return;
        filters.multiValue[position] = {
          enabled: true,
          label: column.title,
          selectOptions: column.filter.selectOptions,
          initialValue: column.filter.initialValue,
        };
      } else {
        filters.multiValue[mobileBehaviour.position] = {
          enabled: true,
          label: column.title,
          selectOptions: column.filter.selectOptions,
          initialValue: column.filter.initialValue,
        };
      }
    }
  });
  return filters;
});

const desktopRowsToResponsiveRows = (desktopRows: DataRow[]): ResponsiveDataRow[] => {
  const indexes = desktopRows.map(row => row.id).map(id => fetchedRows.value.findIndex(row => row.id === id));
  return indexes.map(index => fetchedRows.value[index]);
};

const desktopRowToResponsiveRow = (desktopRow: DataRow): ResponsiveDataRow => {
  const index = fetchedRows.value.findIndex(row => row.id === desktopRow.id);
  return fetchedRows.value[index];
};

const mobileRowsToResponsiveRows = (mobileRows: MobileTableRow[]): ResponsiveDataRow[] => {
  const indexes = mobileRows.map(row => row.id).map(id => fetchedRows.value.findIndex(row => row.id === id));
  return indexes.map(index => fetchedRows.value[index]);
};

const mobileRowToResponsiveRow = (mobileRow: MobileTableRow): ResponsiveDataRow => {
  const index = fetchedRows.value.findIndex(row => row.id === mobileRow.id);
  return fetchedRows.value[index];
};

defineExpose({
  selectRows: (ids: string[]) => {
    if (isMobile.value) {
      mobileTable.value?.selectRows(ids);
    } else {
      desktopTable.value?.selectRows(ids);
    }
  },
  resetAllAndFetch: () => {
    if (isMobile.value) {
      return mobileTable.value?.resetAllAndFetch();
    } else {
      return desktopTable.value?.resetAllAndFetch();
    }
  },
  unselectAllRows: () => {
    if (isMobile.value) {
      mobileTable.value?.unselectAllRows();
    } else {
      desktopTable.value?.unselectAllRows();
    }
  },
  getDataRows: () => {
    return fetchedRows.value;
  },
  getSelectedRows: () => {
    if (isMobile.value) {
      return (mobileTable.value?.getSelectedRows() ?? []).map(mobileRowToResponsiveRow);
    } else {
      return (desktopTable.value?.getSelectedRows() ?? []).map(desktopRowToResponsiveRow);
    }
  },
  triggerDataController: async () => {
    if (isMobile.value) {
      return mobileTable.value?.triggerDataController();
    } else {
      return desktopTable.value?.triggerDataController();
    }
  },
  closeMobileTableItemDetails: () => {
    mobileTable.value?.closeItemDetails();
  },
});
</script>
<template>
  <ElServerSideTable
    v-if="!isMobile"
    ref="desktopTable"
    :columns="props.columns.map(column => ({ ...column, ...column.desktopBehaviour }))"
    :data-controller="desktopDataController"
    :rows-per-page="props.rowsPerPage"
    @rows-selected="data => $emit('rows-selected', desktopRowsToResponsiveRows(data))"
  />
  <ElServerSideMobileTable
    v-else
    ref="mobileTable"
    :color="props.color"
    :selection="mobileSelection"
    :data-controller="mobileDataController"
    :filters="mobileFilters"
    :prevent-default-item-click="props.mobileBehaviour?.preventDefaultItemClick"
    @selection:exit="emit('mobile:selection:exit')"
    @bulk:click="v => emit('mobile:bulk:click', mobileRowsToResponsiveRows(v))"
    @rows-selected="data => $emit('rows-selected', mobileRowsToResponsiveRows(data))"
    @destructive:click="data => $emit('mobile:destructive:click', mobileRowsToResponsiveRows(data))"
    @item:click="row => emit('mobile:item:click', mobileRowToResponsiveRow(row))"
  >
    <slot name="mobile" />
  </ElServerSideMobileTable>
</template>
